import { useEffect, useState } from "react";
import { toast } from "sonner";
import { TrendingUp, Users, MousePointerClick, Layers, Smartphone, Monitor, Tablet, RefreshCw } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";
import AdminShell from "@/components/AdminShell";
import api, { errMsg } from "@/lib/api";

const SRC_COLOR = { facebook: "#1877F2", google: "#EA4335", direct: "#64748B", other: "#0F766E" };
const DEV_ICON = { mobile: Smartphone, tablet: Tablet, desktop: Monitor };

function Tile({ label, value, sub, icon: Icon, tint = "teal", testId }) {
    const tints = {
        teal: "bg-[#0F766E]/10 text-[#0F766E]",
        navy: "bg-[#123B5D]/10 text-[#123B5D]",
        blue: "bg-[#3A80F7]/10 text-[#3A80F7]",
        amber: "bg-[#D97706]/10 text-[#D97706]",
    };
    return (
        <div className="ddi-card ddi-shadow-sm p-4" data-testid={testId}>
            <div className="flex items-center justify-between">
                <span className="text-[11px] uppercase tracking-widest text-[#64748B] font-semibold">{label}</span>
                <span className={"h-8 w-8 rounded-lg inline-flex items-center justify-center " + tints[tint]}>
                    <Icon size={15} />
                </span>
            </div>
            <div className="mt-3 text-[28px] font-bold text-[#123B5D] tracking-tight leading-none">{value}</div>
            {sub && <div className="text-[11px] text-[#64748B] mt-1.5">{sub}</div>}
        </div>
    );
}

export default function SuperAdminWebsiteAnalytics() {
    const [range, setRange] = useState(30);
    const [d, setD] = useState(null);
    const [loading, setLoading] = useState(true);

    async function load() {
        setLoading(true);
        try {
            const r = await api.get(`/super-admin/marketing/analytics?range_days=${range}`);
            setD(r.data);
        } catch (e) { toast.error(errMsg(e, "Failed to load analytics")); }
        finally { setLoading(false); }
    }
    useEffect(() => { load(); /* eslint-disable-line */ }, [range]);

    if (loading || !d) {
        return (
            <AdminShell>
                <div className="animate-fade-up space-y-4">
                    <div className="ddi-skeleton h-8 w-64" />
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                        {Array.from({ length: 5 }).map((_, i) => <div key={i} className="ddi-skeleton h-28" />)}
                    </div>
                    <div className="ddi-skeleton h-80" />
                </div>
            </AdminShell>
        );
    }
    const t = d.tiles;
    const funnelMax = Math.max(1, ...d.funnel.map((f) => f.count));

    return (
        <AdminShell>
            <div className="animate-fade-up" data-testid="analytics-page">
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                        <div className="ddi-eyebrow text-[#0F766E]">Super Admin · Analytics</div>
                        <h1 className="mt-1.5 text-3xl md:text-[34px] font-bold text-[#123B5D] tracking-tight leading-[1.1]">
                            Website Analytics
                        </h1>
                        <p className="mt-2 text-sm text-[#17212B]/70">
                            First-party tracking — sourced from our own web_events + doctors/orders/users tables.
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <select className="ddi-input py-2" value={range} onChange={(e) => setRange(parseInt(e.target.value))}
                            data-testid="analytics-range">
                            <option value={7}>Last 7 days</option>
                            <option value={30}>Last 30 days</option>
                            <option value={90}>Last 90 days</option>
                        </select>
                        <button className="ddi-btn-outline text-sm" onClick={load} data-testid="analytics-refresh">
                            <RefreshCw size={13} className="mr-1.5" /> Refresh
                        </button>
                    </div>
                </div>

                {/* Tiles */}
                <div className="mt-6 grid grid-cols-2 md:grid-cols-5 gap-4">
                    <Tile label="Page views" value={t.total_page_views.toLocaleString()} icon={TrendingUp}
                        tint="teal" testId="tile-page-views" />
                    <Tile label="Unique visitors" value={t.unique_visitors.toLocaleString()} icon={Users}
                        tint="navy" testId="tile-visitors" />
                    <Tile label="Sessions" value={t.total_sessions.toLocaleString()} icon={Layers}
                        tint="blue" testId="tile-sessions" />
                    <Tile label="CTA clicks" value={t.total_cta_clicks.toLocaleString()} icon={MousePointerClick}
                        tint="amber" testId="tile-clicks" />
                    <Tile label="Conversion" value={t.overall_conversion_pct + "%"} sub="visitors → fully-paid"
                        icon={TrendingUp} tint="teal" testId="tile-conv" />
                </div>

                {/* Funnel */}
                <div className="mt-6 ddi-card ddi-shadow-sm p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-lg font-bold text-[#123B5D]">Conversion funnel</h2>
                        <span className="text-[11px] text-[#64748B]">Authoritative counts from the DB · not pixel-derived</span>
                    </div>
                    <div className="space-y-2" data-testid="analytics-funnel">
                        {d.funnel.map((f, i) => {
                            const width = Math.max(4, Math.round((f.count / funnelMax) * 100));
                            const dropStr = i === 0 ? "—" : (f.conversion ?? 0) + "%";
                            return (
                                <div key={f.stage} className="flex items-center gap-3">
                                    <div className="w-40 shrink-0 text-sm text-[#123B5D] font-medium">{f.stage}</div>
                                    <div className="flex-1 bg-[#F5F7FB] rounded-lg overflow-hidden h-9 relative">
                                        <div className={"h-full flex items-center px-3 text-white text-sm font-semibold " +
                                            (i === 0 ? "bg-[#123B5D]" : i < 3 ? "bg-[#3A80F7]" : i < 5 ? "bg-[#0F766E]" : "bg-[#15803D]")}
                                            style={{ width: `${width}%` }}
                                            data-testid={`funnel-bar-${i}`}>
                                            {f.count.toLocaleString()}
                                        </div>
                                    </div>
                                    <div className="w-16 text-right text-xs text-[#64748B] font-semibold">{dropStr}</div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Two-column: sources + devices */}
                <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div className="ddi-card ddi-shadow-sm p-6">
                        <h2 className="text-lg font-bold text-[#123B5D] mb-4">Traffic sources</h2>
                        {d.sources.length === 0 ? (
                            <div className="text-sm text-[#64748B]">No sessions yet in this window.</div>
                        ) : (
                            <ResponsiveContainer width="100%" height={220}>
                                <BarChart data={d.sources}>
                                    <XAxis dataKey="source" tick={{ fontSize: 11 }} />
                                    <YAxis tick={{ fontSize: 11 }} />
                                    <Tooltip cursor={{ fill: "#F5F7FB" }} contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                                    <Bar dataKey="sessions" radius={[6, 6, 0, 0]}>
                                        {d.sources.map((s, i) => (
                                            <Cell key={i} fill={SRC_COLOR[s.source] || SRC_COLOR.other} />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        )}
                    </div>
                    <div className="ddi-card ddi-shadow-sm p-6">
                        <h2 className="text-lg font-bold text-[#123B5D] mb-4">Devices</h2>
                        {d.devices.length === 0 ? (
                            <div className="text-sm text-[#64748B]">No sessions yet in this window.</div>
                        ) : (
                            <ResponsiveContainer width="100%" height={220}>
                                <PieChart>
                                    <Pie data={d.devices} dataKey="count" nameKey="device" outerRadius={80} innerRadius={40}
                                        label={(e) => `${e.device}: ${e.count}`}>
                                        {d.devices.map((_, i) => (
                                            <Cell key={i} fill={["#0F766E", "#3A80F7", "#D9A441"][i % 3]} />
                                        ))}
                                    </Pie>
                                    <Legend wrapperStyle={{ fontSize: 12 }} />
                                </PieChart>
                            </ResponsiveContainer>
                        )}
                    </div>
                </div>

                {/* Top pages + CTA clicks */}
                <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div className="ddi-card ddi-shadow-sm p-6" data-testid="top-pages">
                        <h2 className="text-lg font-bold text-[#123B5D] mb-4">Top pages</h2>
                        <div className="space-y-2">
                            {d.top_pages.length === 0 && <div className="text-sm text-[#64748B]">No page views yet.</div>}
                            {d.top_pages.map((p) => (
                                <div key={p.path} className="flex items-center justify-between border-b border-[#EEF1F6] py-2 last:border-b-0">
                                    <span className="text-sm font-mono text-[#123B5D] truncate max-w-[70%]">{p.path}</span>
                                    <span className="text-sm text-[#17212B] font-semibold">{p.views.toLocaleString()}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="ddi-card ddi-shadow-sm p-6" data-testid="cta-clicks">
                        <h2 className="text-lg font-bold text-[#123B5D] mb-4">CTA clicks</h2>
                        <div className="space-y-2">
                            {d.cta_clicks.length === 0 && <div className="text-sm text-[#64748B]">No CTA click events tracked yet.</div>}
                            {d.cta_clicks.map((c) => (
                                <div key={c.element} className="flex items-center justify-between border-b border-[#EEF1F6] py-2 last:border-b-0">
                                    <span className="text-sm font-mono text-[#123B5D] truncate max-w-[70%]">{c.element}</span>
                                    <span className="text-sm text-[#17212B] font-semibold">{c.clicks.toLocaleString()}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </AdminShell>
    );
}
